import {AddressFormV2Values} from 'presentation/components/ChangeData/Address/AddressFormV2/types';
import {ProfileEnum} from 'presentation/constants';
import {useMediaQueries} from 'presentation/pages';
import {useCallback, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {PredefinedFavoritePlacesEnum, PrincipalFavoritePlaceEnum} from 'domain/usecases';
import {useGetFavoriteAddressesQuery} from '../queries/useGetFavoriteAddressesQuery';
import {postFavoriteAddress} from '../services/postFavoriteAddress';
import {FavoriteAddressesAddRouteState} from './types';

export function useAddFavoriteAddress() {
	const [openAddFavoriteAddressSuccess, setOpenAddFavoriteAddressSuccess] = useState(false);
	const [openAddFavoriteAddressError, setOpenAddFavoriteAddressError] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const {isMobile, isTablet} = useMediaQueries();
	const navigate = useNavigate();
	const {state: routeState} = useLocation();
	const favoriteAddressesRouteState = routeState as FavoriteAddressesAddRouteState | null;
	const queryClient = useQueryClient();

	const {data: favoriteAddresses, isFetched: isFavoriteAddressesFetched} =
		useGetFavoriteAddressesQuery();

	const hasCasaAddress =
		favoriteAddresses?.some(
			item => item.address.flPrincipal === PrincipalFavoritePlaceEnum.YES,
		) ?? false;

	const shouldForceCasaOnly =
		isFavoriteAddressesFetched &&
		(!hasCasaAddress || favoriteAddressesRouteState?.isCasaDeletion);

	const handleOpenFavoriteAddressSuccess = () => setOpenAddFavoriteAddressSuccess(true);

	const handleCloseFavoriteAddressSuccess = () => {
		setOpenAddFavoriteAddressSuccess(false);
		navigate(ProfileEnum.FAVORITE_ADDRESSES_LIST);
	};

	const handleOpenAddFavoriteAddressError = () => setOpenAddFavoriteAddressError(true);
	const handleCloseAddFavoriteAddressError = () => setOpenAddFavoriteAddressError(false);

	const handleBack = useCallback(() => {
		navigate(favoriteAddressesRouteState?.from || ProfileEnum.FAVORITE_ADDRESSES_LIST);
	}, [favoriteAddressesRouteState?.from, navigate]);

	const submitFavoriteAddress = useCallback(
		async (data: AddressFormV2Values) => {
			const isCasa = data.favoriteAddress === PredefinedFavoritePlacesEnum.HOUSE;
			const finalData = {...data, isMainAddress: isCasa};

			setIsSubmitting(true);
			try {
				await postFavoriteAddress({
					data: finalData,
					reverseGeocodeData: favoriteAddressesRouteState?.initialReverseGeocodeData,
				});

				await queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.PROFILE_FAVORITE_ADDRESSES],
				});
				handleOpenFavoriteAddressSuccess();
			} catch {
				handleOpenAddFavoriteAddressError();
			} finally {
				setIsSubmitting(false);
			}
		},
		[favoriteAddressesRouteState, queryClient],
	);

	const onSubmit = useCallback(
		(data: AddressFormV2Values) => {
			submitFavoriteAddress(data);
		},
		[submitFavoriteAddress],
	);

	const initialReverseGeocodeData = favoriteAddressesRouteState?.initialReverseGeocodeData;
	const item = routeState?.item ? routeState.item : null;

	const isMobOrTab = isMobile || isTablet;

	return {
		isMobOrTab,
		initialReverseGeocodeData,
		item,
		handleBack,
		onSubmit,
		openAddFavoriteAddressSuccess,
		handleCloseFavoriteAddressSuccess,
		openAddFavoriteAddressError,
		handleCloseAddFavoriteAddressError,
		isPostingFavoriteAddress: isSubmitting,
		hasCasaAddress,
		shouldForceCasaOnly,
	};
}
